/**
 * Top Games Service
 * Handles the complex business logic for integrating top games with sections
 * Based on the old lobby's LobbyStore.updateTopGames() method
 */

import { SECTION_NAMES } from '@repo/common';
import { useAppStore } from 'store/app/appStore';
import { useCampaignsStore, isTournamentSource } from '../../campaigns/store/campaignsStore';
import type { IParsedTournament } from '../../campaigns/types/variants/tournament.types';
import type IGameSettings from '../../../types/models/IGameSettings';
import type { IGroupGame } from 'shared/utils/gameParsingUtils';
import { parseGameSettings } from 'shared/utils/gameParsingUtils';
import { BANNER_TAG, setBannerTag } from '../constants/gameBanners';
import { MAX_TOP_GAMES } from '../../../constants';

/**
 * Grid blocks count calculation
 * Based on old lobby's gridBlocksCount function
 */
export const gridBlocksCount = (games: IGameSettings[]): number => {
  return games.reduce((prev, curr) => {
    return prev + (curr.banners.lobby.cols > 1 || curr.banners.lobby.rows > 1 ? 1 : 0);
  }, games.length);
};

/**
 * Apply order and mark game as top game
 * Based on old lobby's fixOrders function
 */
const applyOrderAndTopGame = (game: IGameSettings, idx: number): IGameSettings => {
  return {
    ...game,
    gameBanners: setBannerTag(game.gameBanners, BANNER_TAG.TOP, true),
    order: idx + 1,
  };
};

/**
 * Merge games arrays while preserving order
 * Based on old lobby's mergeWithOrdersPreservations function
 */
const mergeWithOrdersPreservations = (
  existingGames: IGameSettings[],
  newGames: IGameSettings[]
): IGameSettings[] => {
  const mergedGames = [...existingGames];
  
  // Add new games that don't exist in the existing array
  newGames.forEach(newGame => {
    const existingIndex = mergedGames.findIndex(game => game.gameKey === newGame.gameKey);
    if (existingIndex === -1) {
      mergedGames.push(newGame);
    }
  });
  
  return mergedGames;
};

/**
 * Update top games section with business logic
 * Based on old lobby's updateTopGames method
 * @param dynamicTopGames - Top games data from API (if not provided, section will be removed)
 */
export const updateTopGames = (dynamicTopGames?: Array<{ gameKey: string; orderNumber: number }>): void => {
  const state = useAppStore.getState();
  const { sections, supportedGames, setSections } = state;
  
  // Find the top section
  const TopGamesSection = sections.find(s => s.name === SECTION_NAMES.TOP);
  
  if (!TopGamesSection) {
    return;
  }

  // The lobby never renders more than MAX_TOP_GAMES Top games, even without a BO topLimit
  const topLimit = Math.min(TopGamesSection.topLimit || MAX_TOP_GAMES, MAX_TOP_GAMES);

  // Check if top limit is already reached
  if (TopGamesSection.games.length >= topLimit) {
    return;
  }

  try {
    if (!dynamicTopGames?.length) {

      const updatedSections = sections.filter(section =>
        section.name !== SECTION_NAMES.TOP
      );
      
      setSections(updatedSections);
      return;
    }
    
    // Filter out games that are already in the section
    const onlyNewGames = dynamicTopGames.filter(
      ({ gameKey }) => !TopGamesSection.games.find(g => g.gameKey === gameKey)
    );
    
    // Check if games are supported
    const supportedGameKeys = Object.keys(supportedGames);
    const onlySupportedGames = onlyNewGames.filter(
      ({ gameKey }) => supportedGameKeys.includes(gameKey)
    );
    
    if (onlySupportedGames.length === 0) {
      return;
    }
    
    // Transform API games to IGroupGame format
    const groupGames: IGroupGame[] = onlySupportedGames
      .sort((a, b) => a.orderNumber - b.orderNumber)
      .map((g, i) => ({
        gameKey: g.gameKey,
        order: i + 1,
        imageLobby: 'vertical' as const,
        imageSection: 'vertical' as const,
      }));
    
    // Parse games to IGameSettings format with topGame flag
    const gamesSettings: IGameSettings[] = groupGames
      .map(game => parseGameSettings({
        mutatedGame: game,
        gameData: supportedGames[game.gameKey],
        forTournament: false,
        isTopGame: true
      }))
      .filter((game): game is IGameSettings => game !== null);

    // We assume that TopGamesSection.games are already sorted by order from BO.
    // If dynamicOrderingByCurrentPlayers is true current players makes effect in the ordering.
    
    // Merge with existing games preserving order
    const mergedGames = mergeWithOrdersPreservations(
      TopGamesSection.games,
      gamesSettings
    );
    
    // Fix orders and apply top limit
    const finalGames = mergedGames
      .map(applyOrderAndTopGame)
      .slice(0, topLimit)
    
    // OPTIMIZATION: Check if games actually changed to prevent unnecessary re-renders
    const gamesChanged = finalGames.length !== TopGamesSection.games.length ||
      finalGames.some((game, idx) => game.gameKey !== TopGamesSection.games[idx]?.gameKey);
    
    if (!gamesChanged) {
      return;
    }
    
    // Create a Set of top game keys for fast lookup
    const topGameKeys = new Set(finalGames.map(game => game.gameKey));
    
    // Update ALL sections to maintain topGame consistency
    const updatedSections = sections.map(section => {
      if (section.name === SECTION_NAMES.TOP) {
        // Update TOP_GAMES section with new games
        return {
          ...section,
          games: finalGames,
          gridBlocks: gridBlocksCount(finalGames),
        };
      } else {
        // Update the TOP banner tag for games in other sections
        const updatedGames = section.games.map(game => ({
          ...game,
          gameBanners: setBannerTag(game.gameBanners, BANNER_TAG.TOP, topGameKeys.has(game.gameKey)),
        }));
        
        return {
          ...section,
          games: updatedGames,
        };
      }
    });
    
    setSections(updatedSections);

    // Update tournament games with topGame flags in the campaigns store
    const campaignsState = useCampaignsStore.getState();
    const hasTournaments = campaignsState.campaigns.some(isTournamentSource);
    if (hasTournaments) {
      const updatedCampaigns = campaignsState.campaigns.map(campaign => {
        if (!isTournamentSource(campaign)) return campaign;
        const parsed = campaign as IParsedTournament;
        if (!parsed.gamesKeys || !Array.isArray(parsed.games)) {
          return campaign;
        }
        const updatedGames = (parsed.games as IGameSettings[]).map(game => ({
          ...game,
          gameBanners: setBannerTag(game.gameBanners, BANNER_TAG.TOP, topGameKeys.has(game.gameKey)),
        }));
        return { ...parsed, games: updatedGames };
      });
      useCampaignsStore.setState({ campaigns: updatedCampaigns });
    }

  } catch (error) {
    // On error, don't update the section - keep existing games
    // Remove TOP_GAMES section when API fails
    const updatedSections = sections.filter(section =>
      section.name !== SECTION_NAMES.TOP
    );
    setSections(updatedSections);
  }
};
